<script setup lang="ts">
import { computed } from 'vue'
import { DeleteDialog } from '@haskoning/vue-component-library'
import OptionsDrawer from '@/components/drawer/OptionsDrawer.vue'
import GIDataCollectionIndex from '@/components/gi-drawer/GIDataCollectionIndex.vue'

import type { GiDataUploadCollectionType } from '@/stores/type/giPoint.type'

import { useCalculationStore } from '@/stores/calculation'
import { useCalculationCPTStore } from '@/stores/calculationCPT'
import { useGiDataUploadCollectionStore } from '@/stores/giDataUploadCollection'
import { useGiPointStore } from '@/stores/giPoints'
import { useOpengroundUIStore } from '@/stores/opengroundUI'
import { useSpeckleImportUIStore } from '@/stores/speckleImportUI'
import { storeToRefs } from 'pinia'

const giDataUploadCollectionStore = useGiDataUploadCollectionStore()
const giPointStore = useGiPointStore()
const calculationStore = useCalculationStore()
const calculationCPTStore = useCalculationCPTStore()
const authorizationStore = useAuthorizationStore()
const opengroundUIStore = useOpengroundUIStore()
const speckleImportUIStore = useSpeckleImportUIStore()

const canAdd = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['add_gidatauploadcollection']
  })
})

const {
  selectedItems: selectedGICollections,
  items: giDataUploadCollectionItems,
  deleteDialog: giCollectionDeleteDialog,
  deleteLoading: giCollectionDeleteLoading
} = storeToRefs(giDataUploadCollectionStore)
const {
  currentItem: currentGIPoint,
  selectedItems: selectedGiPoints,
  deleteDialog: giPointDeleteDialog,
  deleteLoading: giPointDeleteLoading
} = storeToRefs(giPointStore)
const { items: calculationItems } = storeToRefs(calculationStore)

const onRemoveCPTs = (cptIds: number[]) => {
  calculationItems.value.forEach((calculation) => {
    calculation.calculationcpt_set = calculation.calculationcpt_set.filter(
      (calculation) => !cptIds.includes(calculation.cpt)
    )
  })
  cptIds.forEach((cptId) => {
    calculationCPTStore.removeItemById(cptId)
  })
}

async function onGiDataUploadCollectionDelete(deletedItems: GiDataUploadCollectionType[]) {
  calculationStore.clearSelectedItems()
  calculationCPTStore.clearSelectedItems()
  deletedItems.forEach((item) => {
    calculationStore.removeItemsByCollectionId(item.id)
    item.gipoint_set.features.forEach((item) => {
      giPointStore.removeItem(item)
    })
    const cptIds = item.gipoint_set.features.map((item) => item.properties.cpt.id)
    onRemoveCPTs(cptIds)
  })
  giDataUploadCollectionStore.clearSelectedItems()
}

function onGIPointDelete() {
  calculationStore.clearSelectedItems()
  calculationCPTStore.clearSelectedItems()
  if (selectedGiPoints.value.length === 0) {
    return
  }
  if (currentGIPoint.value?.id) {
    currentGIPoint.value = null
  }
  const giPointIds = selectedGiPoints.value.map((item) => item.id)
  giDataUploadCollectionItems.value.forEach((item) => {
    item.gipoint_set.features = item.gipoint_set.features.filter((giPoint) => {
      return !giPointIds.includes(giPoint.id)
    })
  })
  const cptIds = selectedGiPoints.value.map((item) => item.properties.cpt.id)
  onRemoveCPTs(cptIds)
  giPointStore.clearSelectedItems()
  giDataUploadCollectionStore.clearSelectedItems()
}
</script>

<template>
  <OptionsDrawer state-name="giDrawer" :temporary="true">
    <v-card elevation="0" class="px-3 pb-3">
      <v-card-title class="component-title px-0 d-flex align-center ga-2">
        <span>Ground Investigation Data Management</span>
        <div v-if="canAdd" class="d-flex ga-2">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" class="text-none" prepend-icon="mdi-plus" color="primary">
                Add
              </v-btn>
            </template>
            <v-list class="py-0">
              <v-list-item
                prepend-icon="mdi-plus"
                title="Upload files"
                @click="giDataUploadCollectionStore.toggleUploadDialog()"
              >
              </v-list-item>
              <v-list-item
                prepend-icon="mdi-import"
                title="Receive from Speckle"
                @click="speckleImportUIStore.toggleDialog()"
              >
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn
            class="text-none"
            prepend-icon="mdi-import"
            color="primary"
            @click="opengroundUIStore.showDialog = true"
          >
            OG2Speckle
          </v-btn>
        </div>
      </v-card-title>
      <v-card-subtitle class="px-0 component-subtitle">
        Upload files & manage Ground Investigation Data.
      </v-card-subtitle>
    </v-card>
    <v-divider />
    <v-lazy>
      <GIDataCollectionIndex />
    </v-lazy>
    <DeleteDialog
      bulk-delete
      v-model="giCollectionDeleteDialog"
      :loading="giCollectionDeleteLoading"
      :items="selectedGICollections"
      @bulk-delete="giDataUploadCollectionStore.bulkDelete(selectedGICollections)"
      @submitted-and-closed="onGiDataUploadCollectionDelete"
    >
      <template v-slot:info>
        <v-alert
          v-if="giDataUploadCollectionStore.hasCalculationCPTRelatedToSelectedItems()"
          dense
          type="warning"
          variant="outlined"
        >
          GI Points and CPTs associated with the selected GI Data Collections were used in
          calculations. These will be removed as well.
        </v-alert>
      </template>
    </DeleteDialog>
    <DeleteDialog
      bulk-delete
      v-model="giPointDeleteDialog"
      :loading="giPointDeleteLoading"
      :items="selectedGiPoints"
      @bulk-delete="giPointStore.bulkDelete(selectedGiPoints)"
      @submitted-and-closed="onGIPointDelete"
    >
      <template v-slot:info>
        <v-alert
          v-if="giPointStore.hasCalculationCPTRelatedToSelectedItems()"
          dense
          type="warning"
          variant="outlined"
        >
          The selected GI Points and CPTs were used in calculations. These will be removed as well.
        </v-alert>
      </template>
    </DeleteDialog>
  </OptionsDrawer>
</template>

<style scoped>
.resize-handler-gi {
  border-right-width: 10px;
  z-index: 1;
  box-shadow: none;
}

.hidden {
  display: none;
  opacity: 0;
  transition: opacity 0.5s ease;
}

:deep(.v-navigation-drawer__content) {
  overflow-y: hidden;
}

.gi-drawer {
  transition: border-bottom-color 0.3s;
  border-right: 10px solid rgba(0, 0, 0, 0.1);
}
</style>
